import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getDashboard, initiateEMIPayment } from "@/api";
import NotificationPanel from "@/components/NotificationPanel";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import ActivationBanner from "@/components/ActivationBanner";
import BottomNav from "@/components/BottomNav";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { loadRazorpayScript, openRazorpayCheckout } from "@/utils/razorpay";
import { toast } from "sonner";
import { 
  FileText, CreditCard, CheckCircle, Clock, XCircle, 
  LogOut, User, Shield, TrendingUp, Wallet, ChevronRight,
  AlertCircle, Sparkles, Calendar, IndianRupee, History, 
  CreditCard as CardIcon, ArrowUpRight, ArrowDownLeft, Banknote,
  Zap, Target, Timer, PauseCircle
} from "lucide-react";
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingEmi, setPayingEmi] = useState(null);
  const { startPolling } = usePushNotifications();

  useEffect(() => {
    loadDashboard();
    // Preload Razorpay checkout script so Pay Now is instant and never fails with "gateway not loaded"
    loadRazorpayScript().catch(() => {});
    // Push subscription is opt-in via <PushPermissionBanner /> — no automatic native prompt on mount.
    const cleanup = startPolling(30000);
    return cleanup;
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboard = async () => {
    try {
      const res = await getDashboard();
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const handlePayEMI = async (emiId) => {
    setPayingEmi(emiId);
    try {
      // 1. Ensure Razorpay checkout script is loaded (idempotent — no-op if already loaded)
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        toast.error("Unable to load payment gateway. Please check your internet connection and try again.");
        console.error("[Payment] Razorpay script failed to load. Possible causes: network block, ad-blocker, CSP.");
        setPayingEmi(null);
        return;
      }

      // 2. Create order on backend
      const res = await initiateEMIPayment(emiId);
      if (!res.data?.order_id) {
        toast.error(res.data?.detail || "Could not initiate payment");
        setPayingEmi(null);
        return;
      }

      // 3. Open Razorpay checkout via shared helper (UPI Intent enabled)
      const profileForPrefill = data?.profile || {};
      await openRazorpayCheckout({
        order_id: res.data.order_id,
        amount: res.data.amount_paise,
        currency: res.data.currency || "INR",
        key_id: res.data.key_id,
        name: "Rebuilt",
        description: `EMI Payment${res.data.installment_number ? ` — Installment #${res.data.installment_number}` : ''}`,
        userName: profileForPrefill.name || "",
        userMobile: profileForPrefill.mobile || "",
        userEmail: profileForPrefill.email || "",
        onSuccess: (response) => {
          toast.success("Payment successful!");
          console.log("[Payment] success:", response?.razorpay_payment_id);
          setTimeout(() => loadDashboard(), 1500);
        },
        onDismiss: () => {
          toast.info("Payment cancelled");
          setPayingEmi(null);
        },
        onFailure: (error) => {
          const reason = error?.description || error?.reason || "Payment failed";
          // Avoid duplicate toast for user-initiated cancel (handled by onDismiss)
          if (!String(reason).toLowerCase().includes("cancel")) {
            toast.error(reason);
          }
          console.error("[Payment] failed:", error);
          setPayingEmi(null);
        },
      });
    } catch (err) {
      console.error("[Payment] initiation error:", err);
      toast.error(err.response?.data?.detail || "Payment initiation failed");
      setPayingEmi(null);
    }
  };

  if (loading) return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="spinner" style={{ borderTopColor: '#00BCD4' }} />
        <p className="text-white/70 text-sm">Loading dashboard...</p>
      </div>
    </div>
  );

  const profile = data?.profile || {};
  const subscription = data?.subscription;
  const applications = data?.applications || [];
  const completion = data?.profile_completion || 0;
  const upcomingEmis = data?.upcoming_emis || [];
  const repaymentsSummary = data?.repayments_summary || {};
  const paymentHistory = data?.payment_history || [];
  const emiProgress = data?.emi_progress || null;
  const nextEmi = data?.next_emi || null;
  const autopay = data?.autopay || null;

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      success: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      verified: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      active: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Sparkles },
      transferred: { bg: 'bg-cyan-100', text: 'text-cyan-700', icon: TrendingUp },
      paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
      disbursed: { bg: 'bg-cyan-100', text: 'text-cyan-700', icon: TrendingUp },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
        <Icon className="h-3.5 w-3.5" />
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr.substring(0, 10); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-dashboard" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Modern Gradient Header */}
      <header 
        className="sticky top-0 z-50"
        style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}
      >
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON_BLUE} alt="" className="h-10 w-10 bg-white rounded-xl p-1" />
            <div>
              <h1 className="text-lg font-bold text-white tracking-tight" style={{fontFamily:'Poppins, sans-serif'}}>REBUILT</h1>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-medium">Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationPanel />
            <button 
              onClick={handleLogout} 
              data-testid="logout-btn" 
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-5 pb-28">
        {/* Push notification opt-in banner (only renders when permission='default' and not recently dismissed) */}
        <PushPermissionBanner />

        {/* v7.12 — "Payment received — activating your plan" while verification completes */}
        <ActivationBanner subscription={subscription} onActivated={loadDashboard} />

        {/* Welcome Card */}
        <div 
          className="rounded-2xl p-6 text-white relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)' }}
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/10" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <User className="h-7 w-7 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-white/70 text-sm">Welcome back,</p>
                <h2 className="text-xl font-bold tracking-tight" style={{fontFamily:'Poppins, sans-serif'}}>
                  {profile.name || "Customer"}
                </h2>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-white/80 text-sm">
              <Shield className="h-4 w-4" />
              <span>+91 {profile.mobile}</span>
              {profile.kyc_status === 'verified' && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-xs font-medium">
                  <CheckCircle className="h-3 w-3" /> KYC Verified
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Profile Completion */}
        {completion < 100 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Complete Your Profile</span>
              </div>
              <span 
                className="text-2xl font-bold"
                style={{ 
                  background: 'linear-gradient(135deg, #1565C0, #00BCD4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {completion}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${completion}%`,
                  background: 'linear-gradient(90deg, #1565C0, #00BCD4)'
                }}
              />
            </div>
            <button 
              onClick={() => navigate("/customer/onboarding")} 
              className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #1565C0, #00BCD4)' }}
            >
              Continue Setup
            </button>
          </div>
        )}

        {/* Subscription Status Card */}
        {subscription && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: subscription.is_active ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #F59E0B, #D97706)' }}
                >
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{subscription.plan_name || 'EMI Support Plan'}</h3>
                  <p className="text-xs text-gray-500">
                    {subscription.is_active ? 'Active' : subscription.payment_status === 'pending' ? 'Payment Pending' : 'Inactive'}
                  </p>
                </div>
              </div>
              <StatusBadge status={subscription.is_active ? 'active' : subscription.payment_status || 'pending'} />
            </div>
            {subscription.is_active && subscription.days_remaining !== undefined && (
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3 mt-2">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Expires: {formatDate(subscription.expires_at)}</span>
                </div>
                <span className="text-xs font-semibold text-blue-600">{subscription.days_remaining} days left</span>
              </div>
            )}
          </div>
        )}

        {/* NEW: Next EMI Due Card - prominent, color-coded by urgency */}
        {nextEmi && (
          <div
            className="rounded-2xl p-5 text-white relative overflow-hidden shadow-lg"
            style={{
              background: nextEmi.is_overdue
                ? 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)'
                : nextEmi.is_due_today
                  ? 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)'
                  : nextEmi.is_due_soon
                    ? 'linear-gradient(135deg, #D97706 0%, #B45309 100%)'
                    : 'linear-gradient(135deg, #0F766E 0%, #134E4A 100%)'
            }}
          >
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Timer className="h-4 w-4 text-white/80" />
                    <p className="text-[11px] uppercase tracking-widest text-white/80 font-semibold">
                      {nextEmi.is_overdue ? 'EMI Overdue' : 'Next EMI Due'}
                    </p>
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {parseFloat(nextEmi.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </h2>
                  <p className="text-xs text-white/90 mt-1">
                    Installment #{nextEmi.installment_number} &middot; {nextEmi.application_number || 'EMI Plan'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-widest text-white/80 font-semibold">Due Date</p>
                  <p className="text-sm font-bold mt-1">{formatDate(nextEmi.due_date)}</p>
                  {nextEmi.days_remaining !== null && nextEmi.days_remaining !== undefined && (
                    <p className="text-[11px] text-white/80 mt-0.5 font-medium">
                      {nextEmi.is_overdue
                        ? `${Math.abs(nextEmi.days_remaining)} day${Math.abs(nextEmi.days_remaining) !== 1 ? 's' : ''} overdue`
                        : nextEmi.is_due_today
                          ? 'Due today'
                          : `in ${nextEmi.days_remaining} day${nextEmi.days_remaining !== 1 ? 's' : ''}`}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => handlePayEMI(nextEmi.id)}
                disabled={payingEmi === nextEmi.id}
                data-testid="next-emi-pay-btn"
                className="mt-3 w-full py-3 rounded-xl text-sm font-bold bg-white text-gray-900 hover:bg-gray-100 transition-all disabled:opacity-60 shadow-md"
              >
                {payingEmi === nextEmi.id ? 'Processing...' : (autopay?.status === 'active' ? 'Pay Now (Manual Override)' : 'Pay Now')}
              </button>
            </div>
          </div>
        )}

        {/* NEW: EMI Progress Card */}
        {emiProgress && emiProgress.total_installments > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Target className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">EMI Progress</h3>
                  <p className="text-[11px] text-gray-500">{emiProgress.paid} of {emiProgress.total_installments} installments paid</p>
                </div>
              </div>
              <span
                className="text-2xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #1565C0, #00BCD4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {emiProgress.progress_percent}%
              </span>
            </div>
            {/* Segmented progress track */}
            <div className="flex gap-1 mb-4" data-testid="emi-progress-track">
              {Array.from({ length: emiProgress.total_installments }).map((_, i) => {
                let bg = '#E5E7EB';
                if (i < emiProgress.paid) bg = 'linear-gradient(90deg, #10B981, #059669)';
                else if (i < emiProgress.paid + emiProgress.overdue) bg = 'linear-gradient(90deg, #DC2626, #991B1B)';
                return <div key={i} className="flex-1 h-2.5 rounded-full" style={{ background: bg }} />;
              })}
            </div>
            {/* Totals row */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 rounded-lg py-2">
                <p className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">Paid</p>
                <p className="text-sm font-bold text-green-900 mt-0.5">
                  {parseFloat(emiProgress.paid_amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg py-2">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Pending</p>
                <p className="text-sm font-bold text-amber-900 mt-0.5">
                  {parseFloat(emiProgress.pending_amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg py-2">
                <p className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Overdue</p>
                <p className="text-sm font-bold text-red-900 mt-0.5">
                  {parseFloat(emiProgress.overdue_amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* NEW: UPI Autopay Status Card */}
        {autopay && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: autopay.status === 'active'
                      ? 'linear-gradient(135deg, #10B981, #059669)'
                      : autopay.status === 'failed'
                        ? 'linear-gradient(135deg, #DC2626, #991B1B)'
                        : 'linear-gradient(135deg, #6B7280, #4B5563)'
                  }}
                >
                  {autopay.status === 'active' ? (
                    <Zap className="h-5 w-5 text-white" />
                  ) : autopay.status === 'failed' ? (
                    <XCircle className="h-5 w-5 text-white" />
                  ) : (
                    <PauseCircle className="h-5 w-5 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">UPI Autopay</h3>
                  <p className="text-[11px] text-gray-500 capitalize">
                    {autopay.status === 'active' ? 'Mandate active — auto-debit on due date' :
                     autopay.status === 'setup_pending' ? 'Setup pending approval from bank' :
                     autopay.status === 'failed' ? 'Mandate failed — action required' :
                     autopay.status === 'cancelled' ? 'Mandate cancelled' :
                     'Not set up yet'}
                  </p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                autopay.status === 'active' ? 'bg-green-100 text-green-700' :
                autopay.status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {autopay.status.replace('_', ' ')}
              </span>
            </div>
            {(autopay.status === 'active' || autopay.status === 'setup_pending') && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Per-EMI</p>
                  <p className="text-xs font-bold text-gray-900 mt-0.5">
                    {parseFloat(autopay.per_installment_amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Next Charge</p>
                  <p className="text-xs font-bold text-gray-900 mt-0.5">
                    {autopay.next_charge_date ? formatDate(autopay.next_charge_date) : (autopay.first_payment_date ? formatDate(autopay.first_payment_date) : 'TBD')}
                  </p>
                </div>
              </div>
            )}
            {autopay.status !== 'active' && autopay.application_id && (
              <button
                onClick={() => navigate(`/customer/contract/${autopay.application_id}`)}
                className="mt-3 w-full py-2 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-md"
                style={{ background: 'linear-gradient(135deg, #1565C0, #00BCD4)' }}
              >
                {autopay.status === 'failed' ? 'Retry Setup' : 'Complete Setup'}
              </button>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3">
          <button 
            onClick={() => navigate("/customer/application")}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left hover:shadow-md hover:-translate-y-1 transition-all group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Apply</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">EMI Assistance</p>
          </button>
          
          <button 
            onClick={() => navigate("/customer/repayments")}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left hover:shadow-md hover:-translate-y-1 transition-all group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <Banknote className="h-5 w-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Repayments</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Pay EMIs</p>
          </button>

          <button 
            onClick={() => navigate("/customer/transactions")}
            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left hover:shadow-md hover:-translate-y-1 transition-all group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Transactions</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">History</p>
          </button>
        </div>

        {/* Repayment Summary Cards */}
        {(repaymentsSummary.total_due > 0 || repaymentsSummary.total_paid > 0 || repaymentsSummary.total_overdue > 0) && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
              <IndianRupee className="h-4 w-4 text-amber-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{repaymentsSummary.total_due?.toLocaleString('en-IN')}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Due</p>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
              <CheckCircle className="h-4 w-4 text-green-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{repaymentsSummary.total_paid?.toLocaleString('en-IN')}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Paid</p>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
              <AlertCircle className="h-4 w-4 text-red-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900">{repaymentsSummary.total_overdue?.toLocaleString('en-IN')}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Overdue</p>
            </div>
          </div>
        )}

        {/* Upcoming EMIs */}
        {upcomingEmis.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Upcoming EMIs</h3>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {upcomingEmis.slice(0, 3).map((emi, idx) => (
                <div key={emi.id || idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">#{emi.installment_number}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {emi.application_number ? `${emi.application_number}` : `Installment #${emi.installment_number}`}
                      </p>
                      <p className="text-xs text-gray-500">Due: {formatDate(emi.due_date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">
                      {parseFloat(emi.amount).toLocaleString('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0})}
                    </p>
                    <button 
                      onClick={() => handlePayEMI(emi.id)}
                      disabled={payingEmi === emi.id}
                      className="mt-1 text-xs font-semibold text-white px-3 py-1 rounded-full transition-all hover:shadow-md disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                    >
                      {payingEmi === emi.id ? '...' : 'Pay Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {upcomingEmis.length > 3 && (
              <button 
                onClick={() => navigate("/customer/transactions")}
                className="w-full py-3 text-center text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-100"
              >
                View All ({upcomingEmis.length}) <ChevronRight className="inline h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Applications */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-purple-600" />
                <h3 className="font-semibold text-gray-900 text-sm">My Applications</h3>
              </div>
              {applications.length > 0 && (
                <span className="text-xs text-gray-500">{applications.length} total</span>
              )}
            </div>
          </div>
          
          {applications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm mb-4">No applications yet</p>
              <button 
                onClick={() => navigate("/customer/application")}
                className="px-6 py-2 rounded-full text-sm font-semibold text-white transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #1565C0, #00BCD4)' }}
              >
                Apply Now
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {applications.slice(0, 3).map(app => (
                <div 
                  key={app.id} 
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/customer/contract/${app.id}`)}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <span className="text-xs font-mono text-gray-400">{app.application_number || ''}</span>
                      <p className="text-sm font-semibold text-gray-900">
                        {parseFloat(app.amount_requested || app.loan_amount || 0).toLocaleString('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0})}
                      </p>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{app.tenure || 1} month(s)</span>
                    <span>EMI: {parseFloat(app.emi_amount || 0).toLocaleString('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0})}</span>
                    <ChevronRight className="h-3.5 w-3.5 ml-auto text-gray-400" />
                  </div>
                </div>
              ))}
              {applications.length > 3 && (
                <button 
                  onClick={() => navigate("/customer/application")}
                  className="w-full py-3 text-center text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  View All Applications <ChevronRight className="inline h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Payment History */}
        {paymentHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-gray-900 text-sm">Recent Payments</h3>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {paymentHistory.slice(0, 5).map((payment, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      payment.type === 'subscription' ? 'bg-purple-50' :
                      payment.type === 'emi' ? 'bg-blue-50' : 'bg-green-50'
                    }`}>
                      {payment.type === 'subscription' ? (
                        <CardIcon className="h-4 w-4 text-purple-600" />
                      ) : payment.type === 'emi' ? (
                        <ArrowUpRight className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDownLeft className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{payment.description}</p>
                      <p className="text-xs text-gray-500">{formatDate(payment.date)}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-sm text-gray-900">
                    {parseFloat(payment.amount).toLocaleString('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0})}
                  </span>
                </div>
              ))}
            </div>
            {paymentHistory.length > 5 && (
              <button 
                onClick={() => navigate("/customer/transactions")}
                className="w-full py-3 text-center text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-100"
              >
                View All Transactions <ChevronRight className="inline h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Special Offers Card */}
        <button 
          onClick={() => navigate("/customer/offers")}
          className="w-full rounded-2xl p-5 text-left relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FFB300 100%)' }}
        >
          <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/20" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">Special Offers</h3>
              <p className="text-xs text-white/80">Check exclusive deals for you</p>
            </div>
            <ChevronRight className="h-5 w-5 text-white" />
          </div>
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
